/** AI token usage for admins. A super-admin sees per-tenant totals across the
 *  platform; a tenant-admin sees their own learners' usage this month. Both
 *  read from the same page — the role picks the data source and title.
 *
 *  Tokens alone answer nothing: nobody can tell whether 40,000 of them is a
 *  problem. So every row carries what it COST, and the super-admin gets the
 *  month's total against what selling credits recovered — which is the
 *  actual question behind this screen: is a ₹250 subscription profitable?
 */
import { useEffect, useState } from "react";
import {
  platformCost,
  platformTenantUsage,
  tenantLearnerUsage,
  type PlatformCost,
} from "../../api/aiUsage";
import { useAuth } from "../../auth/AuthContext";
import { Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { rupeeCost, rupees } from "../../utils/rupees";

interface Row {
  key: string;
  name: string;
  sub: string | null;
  tokens: number;
  costMinor: number;
}

export function AiUsagePage() {
  const t = useT();
  const { isSuperAdmin } = useAuth();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [cost, setCost] = useState<PlatformCost | null>(null);

  useEffect(() => {
    const load = isSuperAdmin
      ? platformTenantUsage().then((ts) =>
          ts.map((r) => ({
            key: r.tenant_id, name: r.name, sub: null,
            tokens: r.tokens_this_month, costMinor: r.cost_minor,
          })),
        )
      : tenantLearnerUsage().then((ls) =>
          ls.map((r) => ({
            key: r.user_id, name: r.full_name, sub: r.email,
            tokens: r.tokens_this_month, costMinor: r.cost_minor,
          })),
        );
    load.then(setRows).catch(() => setRows([]));
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    // A missing total must not blank the roster below it.
    platformCost().then(setCost).catch(() => setCost(null));
  }, [isSuperAdmin]);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold">
        {t(isSuperAdmin ? "usage.tenants" : "usage.learners")}
      </h1>

      {cost && (
        <Card title={t("usage.thisMonth")}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <p className="text-2xl font-extrabold text-ink">
                {rupeeCost(cost.cost_minor)}
              </p>
              <p className="text-xs uppercase text-muted">{t("usage.aiCost")}</p>
            </div>
            <div>
              <p className="text-2xl font-extrabold text-ink">
                {rupees(cost.topped_up_minor)}
              </p>
              <p className="text-xs uppercase text-muted">{t("usage.toppedUp")}</p>
            </div>
            <div>
              <p className="text-2xl font-extrabold text-ink tabular-nums">
                {cost.tokens_this_month.toLocaleString()}
              </p>
              <p className="text-xs uppercase text-muted">{t("usage.tokensThisMonth")}</p>
            </div>
            {/* Cost against top-ups IS the margin, so say it rather than
                leaving the founder to subtract two tiles in his head. */}
            <div>
              <p className="text-2xl font-extrabold text-ink">
                {rupeeCost(cost.topped_up_minor - cost.cost_minor)}
              </p>
              <p className="text-xs uppercase text-muted">{t("usage.margin")}</p>
            </div>
          </div>
        </Card>
      )}

      <Card>
        {rows === null ? (
          <div className="grid place-items-center py-8">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message={t("usage.none")} />
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase text-muted">
                  <th className="py-2 font-semibold">{t("usage.name")}</th>
                  <th className="py-2 text-right font-semibold">{t("usage.tokensThisMonth")}</th>
                  <th className="py-2 text-right font-semibold">{t("usage.cost")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-line/50">
                    <td className="py-2">
                      <div className="font-semibold text-ink">{r.name}</div>
                      {r.sub && <div className="text-xs text-muted">{r.sub}</div>}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted">
                      {r.tokens.toLocaleString()}
                    </td>
                    <td className="py-2 text-right tabular-nums font-semibold text-ink">
                      {rupeeCost(r.costMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
